
import {
    Component, PropSync, Vue, Watch,
} from 'vue-property-decorator';
import { Getter, Mutation } from 'vuex-class';
import { IInvoice, INettingItem } from '@/api/types/revenue';
import { getOneAdvertiserAffiliates } from '@/api/revenue';
import { getCurrencyRate } from '@/api/catalogue';
import { NETTING_TYPE } from '@/mappings/netting-type';
import { convertBalance } from '@/utils/convertBalance';
import { getCurrencyIcon } from '@/utils/translate';
import { showServerError } from '@/utils';
import FormModal from '@/components/base/FormModal.vue';
import EditModeTextField from '@/components/base/form/EditModeTextField.vue';
import { IEmployeesBalance } from '@/api/types/affiliate-balance';
import { getEmployeesBalances } from '@/api/affiliate-balance';

// строка из ответа /employees/balances (партнёр × валюта)
interface IBalanceRow {
    affiliate_id: number;
    currency: string;
    balance: number;
}

// UI-строка «Выбранные балансы». В invoice.netting уходит уже в форме контракта (см. buildNettingItem)
interface IWorkingRow {
    affiliate_id: number;
    source_currency: string;
    balance: number;                 // текущий баланс партнёра (source), только для UI
    rate: number | string;           // '-' при совпадении валют
    payment_sum: number;             // «Сумма оплаты» в валюте счёта (target)
    id?: number;                     // при редактировании существующей строки
}

@Component({ components: { FormModal, EditModeTextField } })
export default class NettingBalance extends Vue {
    @PropSync('invoice', { required: true }) invoiceSync!: IInvoice;
    @PropSync('totalSumOfNetting', { required: true }) totalSumOfNettingSync!: number;
    @PropSync('nettingErrorText', { required: true }) nettingErrorTextSync!: string;

    @Getter('GET_EDITING_MODE') editingMode!: boolean;
    @Mutation('SET_UNSAVED_DATA_STATE') setUnsavedDataState!: (v: boolean) => void;

    NETTING_TYPE = NETTING_TYPE;
    balanceRows: IBalanceRow[] = [];
    rows: IWorkingRow[] = [];
    deletingRow: IWorkingRow | null = null;

    sourceHeaders = [
        { text: 'Партнёр', value: 'affiliate_id' },
        { text: 'Валюта', value: 'currency' },
        { text: 'Баланс', value: 'balance', align: 'right' },
        { text: '', value: 'action', align: 'right', sortable: false },
    ];

    selectedHeaders = [
        { text: 'Партнёр', value: 'affiliate_id' },
        { text: 'Валюта', value: 'source_currency' },
        { text: 'Текущий баланс', value: 'balance', align: 'right' },
        { text: 'Курс', value: 'rate', align: 'right' },
        { text: 'Доступно к списанию', value: 'after', align: 'right' },
        { text: 'Списание в исх. валюте', value: 'write_off_source', align: 'right' },
        { text: 'Сумма оплаты', value: 'payment_sum', align: 'right' },
        { text: '', value: 'action', align: 'right', sortable: false },
    ];

    viewHeaders = [
        { text: 'Партнёр', value: 'affiliate_id' },
        { text: 'Баланс', value: 'source_currency' },
        { text: 'Исходная сумма', value: 'amount', align: 'right' },
        { text: 'Курс пересчёта', value: 'rate', align: 'right' },
        { text: 'Сумма списания', value: 'paid_sum', align: 'right' },
    ];

    // строки контракта (для просмотра сохранённого счёта)
    get selectedRows(): INettingItem[] {
        return this.invoiceSync.netting!.filter((n) => n.type === NETTING_TYPE.BALANCE);
    }

    isSelected(row: IBalanceRow): boolean {
        return this.rows.some(
            (r) => r.affiliate_id === row.affiliate_id && r.source_currency === row.currency,
        );
    }

    // баланс после пересчёта в валюту счёта (target). Операция всегда multiply
    afterConversion(row: IWorkingRow): number {
        if (row.source_currency === this.invoiceSync.currency) return row.balance || 0;
        return convertBalance(row.balance || 0, row.rate ?? 0);
    }

    // «Списание в исх. валюте»: «Сумма оплаты» (target) обратно в валюту партнёра (source) = target / rate
    writeOffSource(row: IWorkingRow): number {
        const paymentSum = Number(row.payment_sum) || 0;
        if (row.source_currency === this.invoiceSync.currency) return paymentSum;
        const rate = Number(row.rate) || 0;
        return rate ? Math.round((paymentSum / rate) * 100) / 100 : 0;
    }

    async created(): Promise<void> {
        if (this.editingMode) {
            await this.loadBalances();
            this.initRowsFromInvoice();
        }
    }

    // счёт открывается в просмотре (editingMode=false) — created пропускает init.
    // подхватываем момент включения редактирования и подставляем строки из контракта
    @Watch('editingMode')
    async onEditingModeEnabled(enabled: boolean): Promise<void> {
        if (enabled && !this.rows.length) {
            await this.loadBalances();
            this.initRowsFromInvoice();
        }
    }

    async loadBalances(): Promise<void> {
        const advertiserId = this.invoiceSync.advertiser?.id;
        if (!advertiserId) { this.balanceRows = []; return; }
        try {
            const affiliates = await getOneAdvertiserAffiliates(advertiserId);
            const affiliateIds = affiliates.map((a) => a.id);
            if (!affiliateIds.length) { this.balanceRows = []; return; }
            const data: IEmployeesBalance[] = await getEmployeesBalances(affiliateIds);
            // разворачиваем партнёр × валюта (допущение 3в)
            this.balanceRows = data.flatMap((emp) => (emp.balances || []).map((b) => ({
                affiliate_id: emp.affiliate_id,
                currency: b.currency,
                balance: b.balance,
            })));
        } catch (err) {
            showServerError(err, 'Не удалось загрузить балансы партнёров');
        }
    }

    // восстановление UI-строк из сохранённого контракта (при редактировании)
    initRowsFromInvoice(): void {
        this.rows = this.selectedRows.map((n) => {
            const sourceCurrency = n.source_currency || (n.currency as string);
            const same = sourceCurrency === this.invoiceSync.currency;
            const rate = n.rate ?? (same ? '-' : 0);
            // «Сумма оплаты» (target): приоритет — сохранённый paid_sum (точное значение в валюте счёта),
            // иначе fallback: same — amount(=target); FX — amount(source) * rate
            const paymentSum = n.paid_sum != null
                ? (Number(n.paid_sum) || 0)
                : (same
                    ? (Number(n.amount) || 0)
                    : convertBalance(Number(n.amount) || 0, Number(n.rate) || 0));
            // живой баланс с бэка уже уменьшен на списание по этой строке — возвращаем его (amount в source),
            // иначе clampPaymentSum не даст ввести сумму, которую бэк бы принял (доступно + уже списанное)
            const reserved = n.id != null ? (Number(n.amount) || 0) : 0;
            return {
                affiliate_id: n.affiliate_id!,
                source_currency: sourceCurrency,
                balance: this.lookupBalance(n.affiliate_id!, sourceCurrency) + reserved,
                rate,
                payment_sum: paymentSum,
                id: n.id,
            };
        });
    }

    lookupBalance(affiliateId: number, currency?: string): number {
        const found = this.balanceRows.find(
            (b) => b.affiliate_id === affiliateId && b.currency === currency,
        );
        return found ? found.balance : 0;
    }

    async addRow(row: IBalanceRow): Promise<void> {
        if (this.isSelected(row)) return;
        const same = row.currency === this.invoiceSync.currency;
        const working: IWorkingRow = {
            affiliate_id: row.affiliate_id,
            source_currency: row.currency,
            balance: row.balance,
            rate: same ? '-' : 0,
            payment_sum: 0,
        };
        // резервируем слот до await: isSelected смотрит на rows, иначе двойной клик до ответа курса даёт дубль
        this.rows.push(working);
        this.setUnsavedDataState(true);
        if (!same) working.rate = await this.fetchRate(row.currency);
        this.syncToInvoice();
    }

    // получение курса (excel -> cb -> api). Операция всегда multiply
    async fetchRate(currencyFrom: string): Promise<number | string> {
        const sources = ['excel', 'cb', 'api'];
        const today = new Date().toISOString().substr(0, 10);
        for (const source of sources) {
            try {
                // eslint-disable-next-line no-await-in-loop
                const data = await getCurrencyRate({
                    source,
                    currency_from: currencyFrom,
                    currency_to: this.invoiceSync.currency,
                    date: today,
                });
                return data.rate || '-';
            } catch (err) {
                if (err.status !== 404) {
                    showServerError(err, 'Не удалось получить информацию по курсу валют');
                    return '-';
                }
            }
        }
        return '-';
    }

    onRateChange(row: IWorkingRow): void {
        this.clampPaymentSum(row);
        this.syncToInvoice();
        this.setUnsavedDataState(true);
    }

    onPaymentSumChange(row: IWorkingRow): void {
        this.clampPaymentSum(row);
        this.syncToInvoice();
        this.setUnsavedDataState(true);
    }

    // «Сумма оплаты» не может превышать «Баланс после пересчёта»
    clampPaymentSum(row: IWorkingRow): void {
        const max = this.afterConversion(row);
        if ((Number(row.payment_sum) || 0) > max) row.payment_sum = max;
    }

    deleteRow(): void {
        const index = this.rows.indexOf(this.deletingRow!);
        if (index !== -1) this.rows.splice(index, 1);
        this.deletingRow = null;
        this.syncToInvoice();
        this.setUnsavedDataState(true);
    }

    // строка UI -> строка контракта netting[]
    buildNettingItem(row: IWorkingRow): INettingItem {
        const paymentSum = Number(row.payment_sum) || 0;
        const same = row.source_currency === this.invoiceSync.currency;
        let item: INettingItem;
        if (same) {
            // валюта баланса = валюта счёта: amount уже в валюте счёта
            item = {
                type: NETTING_TYPE.BALANCE,
                affiliate_id: row.affiliate_id,
                source_currency: row.source_currency,
                amount: paymentSum,
            };
        } else {
            const rate = Number(row.rate) || 0;
            item = {
                type: NETTING_TYPE.BALANCE,
                affiliate_id: row.affiliate_id,
                source_currency: row.source_currency,
                amount: Math.round((paymentSum / rate) * 100) / 100,
                paid_sum: paymentSum,
                rate,
            };
        }
        // id нужен в обеих ветках, иначе при редактировании счёта бэк создаст новую запись вместо update
        if (row.id) item.id = row.id;
        return item;
    }

    // синхронизируем UI-строки в invoice.netting в форме контракта.
    // мутируем массив на месте (splice), а не переприсваиваем: Netting.vue держит на него
    // ссылку в nettingBalanceData и при переключении типа взаимозачёта восстанавливает по ней —
    // переприсваивание оставило бы кэш со старой (пустой) ссылкой и терялись бы строки
    syncToInvoice(): void {
        const items = this.rows.map((r) => this.buildNettingItem(r));
        this.invoiceSync.netting!.splice(0, this.invoiceSync.netting!.length, ...items);
    }

    // Итого = сумма «Сумма оплаты» (в валюте счёта)
    get total(): number {
        this.totalSumOfNettingSync = this.rows
            .reduce((acc, r) => acc + (Number(r.payment_sum) || 0), 0);
        return this.totalSumOfNettingSync;
    }

    fmt(value: number, currency?: string): string {
        return `${(Number(value) || 0).toLocaleString('ru-RU', {
            maximumFractionDigits: 2, minimumFractionDigits: 2,
        })} ${getCurrencyIcon(currency || (this.invoiceSync.currency as string))}`;
    }
}
